// client/src/components/templates/contratos/TemplateContratoPreview.js - VERSÃO COMPLETA CORRIGIDA
import React, { useState, useRef, useEffect } from 'react';
import { FileText, Download, Printer, Eye, EyeOff, AlertCircle, Loader2, ArrowLeft, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import useAlert from '../../../hooks/useAlert';

const TemplateContratoPreview = ({ template, onClose }) => {
    const { setAlert } = useAlert();
    const [showPreview, setShowPreview] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageContents, setPageContents] = useState([]);
    const iframeRef = useRef(null);

    // Função para dividir conteúdo em páginas
    const splitContentIntoPages = (content) => {
        if (!content) return [''];
        
        // Divide o conteúdo por seções principais (h1, h2, etc.)
        const sections = content.split(/(<h[1-6][^>]*>.*?<\/h[1-6]>)/gi);
        const pages = [];
        let currentPage = '';
        let currentPageSize = 0;
        
        // Tamanho máximo aproximado por página (caracteres)
        const maxPageSize = 2500;
        
        sections.forEach(section => {
            const sectionSize = section.length;
            
            // Se a seção atual + nova seção exceder o tamanho máximo, cria nova página
            if (currentPageSize + sectionSize > maxPageSize && currentPage) {
                pages.push(currentPage);
                currentPage = section;
                currentPageSize = sectionSize;
            } else {
                currentPage += section;
                currentPageSize += sectionSize;
            }
        });
        
        // Adiciona a última página
        if (currentPage) {
            pages.push(currentPage);
        }
        
        console.log(`📄 [DEBUG] Template dividido em ${pages.length} páginas`);
        return pages;
    };

    // Função para criar conteúdo de página individual para preview
    const createPageContent = (pageNumber) => {
        if (!template?.conteudo || pageContents.length === 0) {
            return createErrorContent();
        }

        const pageContent = pageContents[pageNumber - 1] || '';
        
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Template: ${template.nome} - Página ${pageNumber}</title>
                <style>
                    @page {
                        size: A4;
                        margin: 2cm;
                    }
                    
                    body {
                        font-family: Arial, Helvetica, sans-serif;
                        color: #222;
                        line-height: 1.5;
                        margin: 0;
                        padding: 2cm;
                        background: white;
                        min-height: 297mm;
                        box-sizing: border-box;
                    }
                    
                    header {
                        border-bottom: 2px solid #e6e9ee;
                        padding-bottom: 10px;
                        margin-bottom: 20px;
                    }
                    
                    h1 {
                        font-size: 20px;
                        color: #0b63d6;
                        margin: 0;
                    }
                    
                    .meta {
                        font-size: 14px;
                        color: #555;
                    }
                    
                    section {
                        margin-bottom: 14px;
                    }
                    
                    .clause {
                        margin: 8px 0;
                        padding-left: 10px;
                    }
                    
                    footer {
                        margin-top: 30px;
                    }
                    
                    .sign {
                        margin-top: 50px;
                        display: flex;
                        justify-content: space-between;
                    }
                    
                    .sig-block {
                        width: 45%;
                        text-align: center;
                    }
                    
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                    }
                    
                    th, td {
                        border: 1px solid #ddd;
                        padding: 8px;
                        text-align: left;
                    }
                    
                    th {
                        background-color: #f8f9fa;
                    }
                    
                    .page-number {
                        position: fixed;
                        bottom: 1cm;
                        right: 2cm;
                        font-size: 12px;
                        color: #666;
                    }
                </style>
            </head>
            <body>
                ${pageContent}
                <div class="page-number">Página ${pageNumber} de ${totalPages}</div>
            </body>
            </html>
        `;
    };

    // Função para criar conteúdo completo para impressão/PDF
    const createPrintContent = () => {
        if (!template?.conteudo || pageContents.length === 0) {
            return createErrorContent();
        }

        const pagesHTML = pageContents.map((content, index) => `
            <div class="a4-page" style="page-break-after: ${index < pageContents.length - 1 ? 'always' : 'auto'};">
                <div class="page-content">
                    ${content}
                    <div class="page-number">Página ${index + 1} de ${pageContents.length}</div>
                </div>
            </div>
        `).join('');

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Template: ${template.nome}</title>
                <style>
                    @page {
                        size: A4;
                        margin: 2cm;
                    }
                    
                    body {
                        font-family: Arial, Helvetica, sans-serif;
                        color: #222;
                        line-height: 1.5;
                        margin: 0;
                        padding: 0;
                        background: white;
                    }
                    
                    .a4-page {
                        width: 210mm;
                        min-height: 297mm;
                        padding: 2cm;
                        background: white;
                        box-sizing: border-box;
                        position: relative;
                    }
                    
                    .page-content {
                        height: 100%;
                    }
                    
                    header {
                        border-bottom: 2px solid #e6e9ee;
                        padding-bottom: 10px;
                        margin-bottom: 20px;
                    }
                    
                    h1 {
                        font-size: 20px;
                        color: #0b63d6;
                        margin: 0;
                    }
                    
                    .meta {
                        font-size: 14px;
                        color: #555;
                    }
                    
                    section {
                        margin-bottom: 14px;
                    }
                    
                    .clause {
                        margin: 8px 0;
                        padding-left: 10px;
                    }
                    
                    footer {
                        margin-top: 30px;
                    }
                    
                    .sign {
                        margin-top: 50px;
                        display: flex;
                        justify-content: space-between;
                    }
                    
                    .sig-block {
                        width: 45%;
                        text-align: center;
                    }
                    
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                    }
                    
                    th, td {
                        border: 1px solid #ddd;
                        padding: 8px;
                        text-align: left;
                    }
                    
                    th {
                        background-color: #f8f9fa;
                    }
                    
                    .page-number {
                        position: absolute;
                        bottom: 1cm;
                        right: 2cm;
                        font-size: 12px;
                        color: #666;
                    }
                    
                    /* Garantir quebra de página na impressão */
                    @media print {
                        body {
                            margin: 0;
                            padding: 0;
                            background: white;
                        }
                        
                        .a4-page {
                            width: 210mm;
                            height: 297mm;
                            margin: 0;
                            padding: 2cm;
                            page-break-after: always;
                        }
                        
                        .a4-page:last-child {
                            page-break-after: auto;
                        }
                    }
                </style>
            </head>
            <body>
                ${pagesHTML}
            </body>
            </html>
        `;
    };

    // Função para criar conteúdo de erro
    const createErrorContent = () => {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Template: ${template?.nome || 'N/A'}</title>
                <style>
                    @page { 
                        size: A4; 
                        margin: 2cm; 
                    }
                    body { 
                        font-family: Arial, sans-serif; 
                        line-height: 1.6; 
                        margin: 0; 
                        padding: 40px;
                        color: #333;
                        background: #f9f9f9;
                    }
                    .error-container { 
                        background: white;
                        border: 1px solid #e0e0e0;
                        border-radius: 8px;
                        padding: 40px;
                        margin: 50px auto;
                        max-width: 600px;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    }
                    .error-title { 
                        color: #d32f2f; 
                        font-size: 24px; 
                        margin-bottom: 20px;
                        text-align: center;
                    }
                    .error-message {
                        text-align: center;
                        margin-bottom: 30px;
                        color: #666;
                    }
                    .support-message {
                        text-align: center;
                        margin-top: 30px;
                        color: #888;
                        font-style: italic;
                    }
                </style>
            </head>
            <body>
                <div class="error-container">
                    <h1 class="error-title">Template Sem Conteúdo</h1>
                    <p class="error-message">Este template não possui conteúdo para visualização.</p>
                    <p class="support-message">Adicione conteúdo ao template para visualizar a prévia.</p>
                </div>
            </body>
            </html>
        `;
    };

    // Funções de navegação de página
    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Funções de ação
    const handlePrint = () => {
        setIsLoading(true);
        const printContent = createPrintContent();
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
            setAlert('Não foi possível abrir a janela de impressão. Verifique se o pop-up está bloqueado.', 'warning');
            setIsLoading(false);
            return;
        }
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setIsLoading(false);
        }, 500);
    };

    const handleDownload = () => {
        setIsLoading(true);
        const content = createPrintContent();
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `template-${template.nome}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsLoading(false);
    };

    const togglePreview = () => {
        setShowPreview(!showPreview);
    };

    // Processar conteúdo do template e dividir em páginas
    useEffect(() => {
        if (template?.conteudo) {
            const pages = splitContentIntoPages(template.conteudo);
            setPageContents(pages);
            setTotalPages(pages.length);
            setCurrentPage(1);
            console.log(`📄 [DEBUG] Template processado: ${pages.length} páginas criadas`);
        } else {
            setPageContents([]);
            setTotalPages(1);
            setCurrentPage(1);
        }
    }, [template]);

    if (!template) {
        return (
            <div className="content-panel">
                <div className="panel-state-wrapper">
                    <FileText size={48} className="text-gray-400" />
                    <p>Nenhum template selecionado para visualização</p>
                </div>
            </div>
        );
    }

    return (
        <div className="content-panel">
            {/* CABEÇALHO NO ESTILO DO FORMULÁRIO */}
            <div className="form-header">
                <h3 className="form-title">Visualizando: {template.nome}</h3>
                <button 
                    type="button" 
                    onClick={onClose} 
                    title="Fechar" 
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>

            {/* RESUMO DO TEMPLATE */}
            <fieldset className="form-section">
                <legend className="form-section-title">Resumo do Template</legend>
                <div className="form-grid-container-auto-fit">
                    <div className="form-group">
                        <label className="form-label">Nome:</label>
                        <div className="form-input form-input-disabled">{template.nome}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Tipo de Contrato:</label>
                        <div className="form-input form-input-disabled">
                            {template.tipoContrato?.nome || 'Não definido'}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Status:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${template.ativo ? 'badge-green' : 'badge-gray'}`}>
                                {template.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Caracteres:</label>
                        <div className="form-input form-input-disabled highlight">
                            {template.conteudo?.length || 0}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Páginas:</label>
                        <div className="form-input form-input-disabled highlight">
                            {totalPages}
                        </div>
                    </div>
                </div>
            </fieldset>

            {/* CONTROLES DE VISUALIZAÇÃO */}
            <fieldset className="form-section">
                <legend className="form-section-title">Controles de Visualização</legend>
                <div className="editor-toolbar">
                    <button 
                        onClick={togglePreview}
                        className="action-button action-button-secondary"
                        disabled={isLoading}
                    >
                        {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
                    </button>
                    
                    <button 
                        onClick={handleDownload}
                        className="action-button action-button-secondary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        Download
                    </button>
                    
                    <button 
                        onClick={handlePrint}
                        className="action-button action-button-primary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                        Imprimir/PDF
                    </button>
                </div>
            </fieldset>

            {/* PREVIEW DO CONTEÚDO */}
            {showPreview && (
                <fieldset className="form-section">
                    <legend className="form-section-title">
                        Preview do Contrato - Página {currentPage} de {totalPages}
                    </legend>
                    
                    {isLoading ? (
                        <div className="panel-state-wrapper">
                            <Loader2 size={48} className="animate-spin text-blue-500" />
                            <p>Processando template...</p>
                        </div>
                    ) : !template.conteudo ? (
                        <div className="panel-state-wrapper error">
                            <AlertCircle size={48} className="text-yellow-500" />
                            <h4>Template Sem Conteúdo</h4>
                            <p>Este template não possui conteúdo para visualização.</p>
                        </div>
                    ) : pageContents.length > 0 ? (
                        <div className="a4-preview-wrapper">
                            {/* CONTROLES DE PAGINAÇÃO */}
                            <div className="editor-toolbar" style={{ marginBottom: '1rem' }}>
                                <button 
                                    onClick={prevPage}
                                    disabled={currentPage === 1}
                                    className="action-button action-button-secondary"
                                >
                                    <ChevronLeft size={16} />
                                    Anterior
                                </button>
                                
                                <span className="pagination-info" style={{ 
                                    padding: '0.5rem 1rem', 
                                    fontSize: '0.875rem',
                                    color: '#6b7280'
                                }}>
                                    Página {currentPage} de {totalPages}
                                </span>
                                
                                <button 
                                    onClick={nextPage}
                                    disabled={currentPage === totalPages}
                                    className="action-button action-button-secondary"
                                >
                                    Próxima
                                    <ChevronRight size={16} />
                                </button>
                            </div>

                            {/* CONTAINER DA PÁGINA ATUAL */}
                            <div className="single-page-container">
                                <div className="a4-page-single">
                                    <iframe
                                        ref={iframeRef}
                                        srcDoc={createPageContent(currentPage)}
                                        title={`Template: ${template.nome} - Página ${currentPage}`}
                                        className="preview-iframe-single"
                                        sandbox="allow-same-origin"
                                        onLoad={() => setIsLoading(false)}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="panel-state-wrapper">
                            <AlertCircle size={48} className="text-yellow-500" />
                            <p>Template não disponível para visualização</p>
                        </div>
                    )}
                </fieldset>
            )}

            {/* BOTÕES DE AÇÃO NO FINAL */}
            <div className="form-actions">
                <button 
                    type="button" 
                    onClick={onClose}
                    className="action-button action-button-secondary"
                >
                    <ArrowLeft size={18} />
                    Voltar para Lista
                </button>
            </div>
        </div>
    );
};

export default TemplateContratoPreview;